import { push } from 'redux-first-history';
import { call, put, select } from 'redux-saga/effects';
import {
  PROFILE_ADDRESSES_GET,
  PROFILE_INFO_SAVE,
  PROFILE_ADDRESS_SAVE,
  PROFILE_DOCUMENTS_GET,
  PROFILE_DOC_SAVE,
  PROFILE_ADDRESS_DELETE,
  PROFILE_DOC_DELETE,
  PROFILE_DOC_FILES_GET,
  PROFILE_PIN_SAVE,
} from '../actions/types';
import { userUpdate } from './user';
import { paths } from '../../utils/paths';
import { documentFilePost } from './documents';
import { createQadraSaga } from './shared';
import {
  apiUserDocumentsGet,
  apiUserAddressesGet,
  apiUserAddressPost,
  apiUserDocumentPost,
  apiUserPinPost,
  apiUserPinPatch,
  apiUserPatch,
} from '../api/user';
import {
  apiDocumentDelete,
  apiDocumentPatch,
  apiDocumentFileGet,
  apiDocumentFileDelete,
} from '../api/documents';
import { apiAddressPatch, apiAddressDelete } from '../api/addresses';

function* profileInfoSave({
  payload: {
    name,
    lastname,
    title,
    phonePrefix,
    phoneNumber,
    sex,
    dd,
    mm,
    yyyy,
    taxCode,
    countryOfBirthId,
    allowMarketingEmails,
    politicallyExposedPerson,
    politicalOccupation,
    politicallyExposedRelative,
    politicallyExposedRelativeRelationship,
    politicallyExposedRelativeOccupation,
    status,
    backUrl,
  },
}) {
  const id = yield select((s) => s.app.user.id);

  const { user } = yield call(apiUserPatch, id, {
    title,
    name,
    lastname,
    phonePrefix,
    phoneNumber,
    sex,
    dd,
    mm,
    yyyy,
    taxCode,
    countryOfBirthId,
    allowMarketingEmails,
    politicallyExposedPerson,
    politicalOccupation,
    politicallyExposedRelative,
    politicallyExposedRelativeRelationship,
    politicallyExposedRelativeOccupation,
    status,
  });

  yield put({ type: PROFILE_INFO_SAVE._SUCCESS, payload: { user } });

  yield put(push(backUrl || paths.profile.info));
}

const watchProfileInfoSave = createQadraSaga(profileInfoSave, {
  action: PROFILE_INFO_SAVE._REQUEST,
});

function* profileAddressesGet() {
  const id = yield select((s) => s.app.user.id);
  const { addresses } = yield call(apiUserAddressesGet, id);
  yield put({ type: PROFILE_ADDRESSES_GET._SUCCESS, addresses });
}

const watchProfileAddressesGet = createQadraSaga(profileAddressesGet, {
  action: PROFILE_ADDRESSES_GET._REQUEST,
});

function* profileAddressSave({
  payload: {
    id,
    streetName,
    region,
    postCode,
    city,
    province,
    addressType,
    countryId,
    isResidential,
    backUrl,
  },
}) {
  if (id) {
    yield call(apiAddressPatch, id, {
      streetName,
      region,
      postCode,
      city,
      province,
      addressType,
      countryId,
      isResidential,
    });
  } else {
    const userId = yield select((s) => s.app.user.id);
    yield call(apiUserAddressPost, userId, {
      streetName,
      region,
      postCode,
      city,
      province,
      addressType,
      countryId,
      isResidential,
    });
  }

  yield profileAddressesGet();

  yield put(push(backUrl || paths.profile.addresses));
}

const watchProfileAddressSave = createQadraSaga(profileAddressSave, {
  action: PROFILE_ADDRESS_SAVE._REQUEST,
});

function* profileAddressDelete({ payload: { id } }) {
  yield call(apiAddressDelete, id);
  yield profileAddressesGet();
  yield put(push(paths.profile.addresses));
}

const watchProfileAddressDelete = createQadraSaga(profileAddressDelete, {
  action: PROFILE_ADDRESS_DELETE._REQUEST,
});

function* profileDocsGet() {
  const id = yield select((s) => s.app.user.id);
  const { documents } = yield call(apiUserDocumentsGet, id);
  yield put({ type: PROFILE_DOCUMENTS_GET._SUCCESS, documents });
}

const watchProfileDocsGet = createQadraSaga(profileDocsGet, {
  action: PROFILE_DOCUMENTS_GET._REQUEST,
});

function* profileDocFilesGet({ payload: { doc } }) {
  const resDoc = JSON.parse(JSON.stringify(doc)); // immer 4 freeze doc

  const { documentFiles } = doc;
  if (documentFiles && documentFiles.length) {
    // eslint-disable-next-line no-plusplus
    for (let j = 0; j < documentFiles.length; j++) {
      const f = documentFiles[j];
      const r = yield call(apiDocumentFileGet, f.documentId, f.id);
      //  console.log(r);
      //  if (!documents[i].files) documents[i].files = [];
      resDoc.documentFiles[j] = { ...f, ...r };
    }
  }

  yield put({ type: PROFILE_DOC_FILES_GET._SUCCESS, doc: resDoc });
}

const watchProfileDocFilesGet = createQadraSaga(profileDocFilesGet, {
  action: PROFILE_DOC_FILES_GET._REQUEST,
});

function* profileDocSave({
  payload: {
    id,
    number,
    expiryDate,
    issueDate,
    countryId,
    cityOfBirthId,
    documentTypeId,
    filesToUpload,
    filesToDelete,
    backUrl,
  },
}) {
  let realId = id;
  if (realId) {
    yield call(apiDocumentPatch, id, {
      number,
      expiryDate,
      issueDate,
      countryId,
      cityOfBirthId,
      documentTypeId,
    });
  } else {
    const userId = yield select((s) => s.app.user.id);
    const { document } = yield call(apiUserDocumentPost, userId, {
      number,
      expiryDate,
      issueDate,
      countryId,
      cityOfBirthId,
      documentTypeId,
    });
    realId = document.id;
  }

  // file upload
  if (filesToUpload && filesToUpload.length) {
    for (let i = 0; i < filesToUpload.length; i += 1) {
      yield documentFilePost({ file: filesToUpload[i], documentId: realId, showSpinner: false });
    }
  }

  // file delete
  if (filesToDelete && filesToDelete.length) {
    for (let i = 0; i < filesToDelete.length; i += 1) {
      yield call(apiDocumentFileDelete, id, filesToDelete[i]);
    }
  }

  yield profileDocsGet();

  yield put(push(backUrl || paths.profile.docs));
}

const watchProfileDocSave = createQadraSaga(profileDocSave, {
  action: PROFILE_DOC_SAVE._REQUEST,
});

function* profileDocDelete({ payload: { id } }) {
  yield call(apiDocumentDelete, id);
  yield profileDocsGet();
}

const watchProfileDocDelete = createQadraSaga(profileDocDelete, {
  action: PROFILE_DOC_DELETE._REQUEST,
});

function* profilePinSave({ payload: { pin, backUrl } }) {
  const user = yield select((s) => s.app.user);

  if (user.hasPin) {
    yield call(apiUserPinPatch, user.id, { pin });
  } else {
    yield call(apiUserPinPost, user.id, { pin });
  }
  yield userUpdate();

  if (backUrl) yield put(push(backUrl));
}

const watchProfilePinSave = createQadraSaga(profilePinSave, {
  action: PROFILE_PIN_SAVE._REQUEST,
});

export default [
  watchProfileInfoSave,
  watchProfileAddressesGet,
  watchProfileAddressSave,
  watchProfileAddressDelete,
  watchProfileDocsGet,
  watchProfileDocSave,
  watchProfileDocDelete,
  watchProfileDocFilesGet,
  watchProfilePinSave,
];
